import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { loadStripe } from '@stripe/stripe-js';
import { getStripeKey } from '../config/stripe';
import './Checkout.css';

// Load Stripe
const stripeKey = getStripeKey();
console.log('Loading Stripe with key:', stripeKey.substring(0, 20) + '...');
const stripePromise = loadStripe(stripeKey);

// Checkout Button Component
const CheckoutButton = ({ cart, total }) => {
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    
    try {
      console.log('Creating checkout session with items:', cart);
      
      const response = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: cart.map(item => ({
            id: item.id,
            title: item.fullTitle || item.title,
            price: item.priceInCents,
            quantity: item.quantity,
            image: item.image,
            selectedSize: item.selectedSize,
            selectedType: item.selectedType
          })),
          total: total
        }),
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
          console.error('Server error:', errorData);
          throw new Error(`Server error: ${errorData.error || response.statusText}`);
        } catch (jsonError) {
          console.error('JSON parsing error:', jsonError);
          console.error('Response status:', response.status);
          console.error('Response status text:', response.statusText);
          console.error('Response URL:', response.url);
          throw new Error(`Server error: ${response.status} ${response.statusText} - URL: ${response.url}`);
        }
      }

      const responseData = await response.json();
      console.log('Checkout session response:', responseData);
      
      if (responseData.error) {
        throw new Error(`Server error: ${responseData.error} - ${responseData.details || ''}`);
      }
      
      const { sessionId, url } = responseData;
      console.log('Checkout session created:', sessionId);
      console.log('Checkout URL:', url);
      
      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionId,
      });

      if (error) {
        console.error('Stripe error:', error);
        alert(`Checkout error: ${error.message}`);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert(`Checkout failed: ${err.message}`);
    }
    
    setLoading(false);
  };

  return (
    <button 
      onClick={handleCheckout}
      disabled={loading}
      className="checkout-button"
    >
      {loading ? 'Redirecting to checkout...' : 'Proceed to Checkout'}
    </button>
  );
};

// Shipping Information Component
const ShippingInfo = ({ isOpen, onToggle }) => (
  <div className="info-section">
    <button className="info-toggle" onClick={onToggle}>
      <span>Shipping Information</span>
      <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
    </button>
    {isOpen && (
      <div className="info-content">
        <p><strong>All orders ship within 1 to 2 weeks after purchase</strong></p>
        
        <div className="shipping-regions">
          <div className="region">
            <h4>NL:</h4>
            <p>Shipping within the Netherlands is handled exclusively by PostNL. Once shipped, delivery within the Netherlands typically takes 1–2 working days.</p>
          </div>
          
          <div className="region">
            <h4>EU:</h4>
            <p>Shipping within Europe is handled exclusively by DHL Express. Once shipped delivery within Europe typically takes 1–3 working days.</p>
          </div>
          
          <div className="region">
            <h4>World:</h4>
            <p>Shipping outside of Europe is handled exclusively by DHL Express International. Once shipped delivery typically takes 2–5 working days depending on your location.</p>
          </div>
        </div>
        
        <div className="framing-note">
          <p><strong>If you choose the framed option, please allow an additional 2 to 3 weeks for production. All frames are custom-made to order to ensure the highest quality.</strong></p>
        </div>
      </div>
    )}
  </div>
);

// Artwork Handling Component
const ArtworkHandling = ({ isOpen, onToggle }) => (
  <div className="info-section">
    <button className="info-toggle" onClick={onToggle}>
      <span>Artwork Handling</span>
      <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
    </button>
    {isOpen && (
      <div className="info-content">
        <p>The works are shipped unframed unless you've selected the framed option.</p>
        <p>Once received, carefully unpack, frame to your preference, and display in a dry, well-lit space away from direct sunlight.</p>
      </div>
    )}
  </div>
);

// Taxes and Customs Component
const TaxesCustoms = ({ isOpen, onToggle }) => (
  <div className="info-section">
    <button className="info-toggle" onClick={onToggle}>
      <span>Taxes & Customs</span>
      <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
    </button>
    {isOpen && (
      <div className="info-content">
        <p><strong>Taxes and custom duties</strong> All applicable taxes, customs duties, and import fees are the responsibility of the customer and are not included in the item price or shipping cost. These charges may vary depending on your country's regulations.</p>
      </div>
    )}
  </div>
);



const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const [shippingInfoOpen, setShippingInfoOpen] = useState(false);
  const [artworkHandlingOpen, setArtworkHandlingOpen] = useState(false);
  const [taxesCustomsOpen, setTaxesCustomsOpen] = useState(false);

  const total = getCartTotal();
  const totalDollars = (total / 100).toFixed(2);

  const handlePaymentSuccess = () => {
    clearCart();
    alert('Payment successful! Thank you for your purchase.');
    // Redirect to home or order confirmation page
    window.location.href = '/';
  };

  if (cart.length === 0) {
    return (
      <div className="checkout">
        <div className="container">
          <div className="empty-checkout">
            <h1>Your cart is empty</h1>
            <p>Please add some items to your cart before proceeding to checkout.</p>
            <a href="/shop" className="btn btn-accent">Continue Shopping</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout">
      <div className="container">
        <div className="checkout-content">
                  <div className="checkout-main">
          <h1>Checkout</h1>
          
          <div className="checkout-summary">
            <p>Review your order and proceed to secure checkout with Stripe.</p>
            <CheckoutButton cart={cart} total={total} />
          </div>
        </div>

          <div className="checkout-sidebar">
            <h3>Order Summary</h3>
            <div className="order-items">
              {cart.map((item, index) => (
                <div key={index} className="order-item">
                  <img src={item.image} alt={item.title} />
                  <div className="order-item-details">
                    <h4>{item.fullTitle || item.title}</h4>
                    <p className="order-item-price">{item.price}</p>
                    {item.selectedSize && (
                      <p className="order-item-size">
                        {item.selectedSize} - {item.selectedType === 'somerset' ? 'Somerset Giclée' : 'Canvas Giclée'}
                      </p>
                    )}
                    {item.quantity > 1 && (
                      <p className="order-item-quantity">Qty: {item.quantity}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="order-summary">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>${totalDollars}</span>
              </div>
              <div className="summary-row">
                <span>Shipping</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>${totalDollars}</span>
              </div>
            </div>

            <div className="info-sections">
              <h3>Additional Information</h3>
              <ShippingInfo 
                isOpen={shippingInfoOpen}
                onToggle={() => setShippingInfoOpen(!shippingInfoOpen)}
              />
              
              <ArtworkHandling 
                isOpen={artworkHandlingOpen}
                onToggle={() => setArtworkHandlingOpen(!artworkHandlingOpen)}
              />
              
              <TaxesCustoms 
                isOpen={taxesCustomsOpen}
                onToggle={() => setTaxesCustomsOpen(!taxesCustomsOpen)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout; 